import { type FormEvent, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Dayjs } from "dayjs"

import {
  useCancelScheduledAdminMessageMutation,
  useCreateAdminMessageTemplateMutation,
  useDeleteAdminMessageTemplateMutation,
  useDuplicateAdminMessageTemplateMutation,
  useGetAdminMessageHistoryQuery,
  useGetAdminMessageQuery,
  useGetAdminMessageTemplateQuery,
  useGetAdminMessageTemplatesQuery,
  useGetAdminScheduledMessagesQuery,
  useLazyGetAdminMessageTemplatesQuery,
  useScheduleAdminMessagesMutation,
  useSendAdminMessagesNowMutation,
  useSendScheduledAdminMessageNowMutation,
  useUpdateAdminMessageTemplateMutation,
} from "store/api/adminMessagesApi"
import { UITypes } from "types"
import { ReactComponent as ArrowLeftIcon } from "assets/svg/icon-arrow-left.svg"
import { ReactComponent as DeleteIcon } from "assets/svg/icon-delete.svg"
import { ReactComponent as PencilIcon } from "assets/svg/icon-pencil.svg"
import { ReactComponent as PlusIcon } from "assets/svg/icon-plus.svg"

import { parseDateField } from "utils/dateUtils"
import getErrorMessage from "utils/getErrorMessage"
import { getMessageTimezone, isFutureMessageDate } from "utils/messageScheduling"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import { MarkdownEditor } from "components/form/MarkdownEditor"
import { SelectDynamic } from "components/form/SelectDynamic"
import InfoBanner from "components/InfoBanner"
import TableWrapper from "components/table/TableWrapper"
import MessageScheduleDateTimePicker from "pages/contractor/MessageCenter/MessageScheduleDateTimePicker"

import { AdminMessageActionDialog } from "./AdminMessageActionDialog"
import { adminMessageTableHeaderCells, convertAdminMessageDevicesToTableRows } from "./adminMessageTable"
import { useAdminDeviceMessages } from "./useAdminDeviceMessages"

import styles from "./index.module.scss"

const historyHeaderCells: UITypes.TableHeaderCell[] = [
  { title: "Title", name: "title", type: "sort-search", size: "middle" },
  { title: "Message", name: "message", type: "sort-search", size: "middle" },
  { title: "Creation date", name: "creation_date", type: "sort", size: "middle" },
  { title: "Last Delivery Date", name: "last_delivery_date", type: "sort", size: "middle" },
  { title: "Delivered", name: "delivered", type: "sort", size: "small" },
  { title: "Pending", name: "pending", type: "sort", size: "small" },
  { title: "Failed", name: "failed", type: "sort", size: "small" },
  { title: "Status", name: "status", type: "sort", size: "small" },
]

const getMessageCenterPageSize = (params: UITypes.Params) => Number(params.perPage ?? params.per_page) || 50

const batchRows = (
  batches: Array<{
    id: number
    title?: string
    status: string
    contentPlain?: string | null
    contentRich?: string | null
    scheduledAt?: string | null
    actualSentAt?: string | null
    createdAt?: string | null
    deliveredCount?: number
    pendingCount?: number
    failedCount?: number
    scheduled_at?: string | null
    created_at?: string
    delivered?: number
    pending?: number
    failed?: number
  }>,
): UITypes.TableBodyRow[] =>
  batches.map(batch => ({
    title: { value: batch.title || "-" },
    message: { value: batch.contentPlain || batch.contentRich || "-" },
    creation_date: {
      value:
        parseDateField(batch.createdAt || batch.created_at || batch.scheduledAt || batch.scheduled_at, "L LT") || "-",
    },
    last_delivery_date: {
      value: parseDateField(batch.actualSentAt || batch.scheduledAt || batch.scheduled_at, "L LT") || "-",
    },
    delivered: { value: batch.deliveredCount ?? batch.delivered ?? 0 },
    pending: { value: batch.pendingCount ?? batch.pending ?? 0 },
    failed: { value: batch.failedCount ?? batch.failed ?? 0 },
    status: { value: batch.status },
    rowSettings: { id: batch.id },
  }))

export const VendorMessageCenter = () => {
  const { urlQueryParams } = useUrlQueryParams()
  const pageSize = getMessageCenterPageSize(urlQueryParams)
  const { devices, meta, isLoading, isError } = useAdminDeviceMessages(urlQueryParams)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<UITypes.Option[]>([])
  const [showSchedulePicker, setShowSchedulePicker] = useState(false)
  const [selectedDateTime, setSelectedDateTime] = useState<Dayjs | null>(null)
  const [pendingAction, setPendingAction] = useState<"send" | "schedule" | null>(null)
  const [result, setResult] = useState<{ kind: "success" | "error"; title: string; message: string } | null>(null)
  const [sendNow, { isLoading: isSending }] = useSendAdminMessagesNowMutation()
  const [scheduleMessages, { isLoading: isScheduling }] = useScheduleAdminMessagesMutation()
  const navigate = useNavigate()
  const selectedDeviceSerialNumbers = devices
    .filter(device => selectedDeviceIds.includes(device.id))
    .map(device => device.sn)
  const initiallyExpandedRows = useMemo(
    () =>
      Object.fromEntries(devices.filter(device => device.messages.length > 0).map(device => [String(device.id), true])),
    [devices],
  )
  const isHeaderChecked = devices.length > 0 && devices.every(device => selectedDeviceIds.includes(device.id))
  const timeZone = useMemo(getMessageTimezone, [])

  const toggleDevice = (id: number) => {
    setSelectedDeviceIds(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]))
  }

  const closeAction = () => {
    setPendingAction(null)
    setShowSchedulePicker(false)
  }

  const openSchedulePicker = () => {
    setSelectedDateTime(null)
    setShowSchedulePicker(true)
  }

  const openScheduleConfirmation = () => {
    if (isFutureMessageDate(selectedDateTime)) {
      setShowSchedulePicker(false)
      setPendingAction("schedule")
    }
  }

  const dispatchMessage = async () => {
    const messageTemplateId = selectedTemplate[0]?.value as number
    const request = {
      device_ids: selectedDeviceIds,
      message_template_id: messageTemplateId,
      ...(pendingAction === "schedule" ? { scheduled_at: selectedDateTime?.toISOString(), timezone: timeZone } : {}),
    }

    try {
      const response = pendingAction === "schedule" ? await scheduleMessages(request) : await sendNow(request)
      const error = getErrorMessage(response.error)
      if (error) {
        setPendingAction(null)
        setResult({ kind: "error", title: "Message could not be sent", message: error })
        return
      }

      setResult({
        kind: "success",
        title: pendingAction === "schedule" ? "Message scheduled" : "Message sent",
        message:
          pendingAction === "schedule"
            ? `Your message is scheduled for ${selectedDateTime?.format("MMMM D, YYYY h:mm A")} (${timeZone}).`
            : "Your message was sent to the selected devices.",
      })
      setSelectedDeviceIds([])
      setSelectedTemplate([])
      setSelectedDateTime(null)
      setPendingAction(null)
    } catch (error) {
      setPendingAction(null)
      setResult({
        kind: "error",
        title: "Message could not be sent",
        message: getErrorMessage(error as Parameters<typeof getErrorMessage>[0]) || "Unable to send the message.",
      })
    }
  }

  return (
    <>
      <InfoBanner text="Only devices that are installed and currently online are eligible to receive messages and are listed here for message selection and delivery." />
      <ContentWrapper title="Message center" className={styles.content}>
        <div className={styles.toolbar}>
          <ButtonNew color="blue" onClick={() => navigate("/messages/create-template")}>
            Create Template
          </ButtonNew>
          <SelectDynamic
            className={styles.selectWrapper}
            placeholder="Select the template"
            valueName="id"
            labelName="title"
            values={selectedTemplate}
            onChange={setSelectedTemplate}
            useLazyQuery={useLazyGetAdminMessageTemplatesQuery}
            isSearchable={false}
          />
          <ButtonNew
            color="blue"
            disabled={!selectedTemplate.length || !selectedDeviceIds.length}
            onClick={() => setPendingAction("send")}
          >
            Send now
          </ButtonNew>
          <span className={styles.scheduleAction}>
            <ButtonNew
              color="blueBordered"
              disabled={!selectedTemplate.length || !selectedDeviceIds.length}
              onClick={openSchedulePicker}
            >
              Schedule for later
            </ButtonNew>
            {showSchedulePicker && (
              <MessageScheduleDateTimePicker
                selectedDateTime={selectedDateTime}
                onDateTimeChange={setSelectedDateTime}
                onCancel={() => setShowSchedulePicker(false)}
                onSubmit={openScheduleConfirmation}
                timeZone={timeZone}
              />
            )}
          </span>
          <span className={styles.spacer} />
          <EditColumnsButton tableName="admin_messages_list" tableHeaderCells={adminMessageTableHeaderCells} />
          <ButtonNew onClick={() => navigate("/messages/history")}>History</ButtonNew>
        </div>
        <TableWrapper
          headerCells={adminMessageTableHeaderCells}
          data={convertAdminMessageDevicesToTableRows(devices)}
          isLoading={isLoading}
          emptyMessage={isError ? "Unable to load admin message devices." : undefined}
          rowsExpanded={initiallyExpandedRows}
          currentItemIDs={selectedDeviceIds}
          isHeaderChecked={isHeaderChecked}
          onCheckboxChange={toggleDevice}
          onHeaderCheckboxChange={() => setSelectedDeviceIds(isHeaderChecked ? [] : devices.map(device => device.id))}
          additionalTags={
            selectedDeviceSerialNumbers.length
              ? [{ label: "Selected Device S/N", values: selectedDeviceSerialNumbers }]
              : []
          }
          clearAdditionalTags={() => setSelectedDeviceIds([])}
        />
        <Pagination meta={meta} perPage={pageSize} />
        {pendingAction && (
          <AdminMessageActionDialog
            title={pendingAction === "send" ? "Send message now?" : "Schedule message?"}
            confirmText={pendingAction === "send" ? "Send now" : "Schedule"}
            onConfirm={dispatchMessage}
            onCancel={closeAction}
            isFetching={isSending || isScheduling}
          >
            {pendingAction === "schedule" ? (
              <>
                <p>This message will be sent to {selectedDeviceIds.length} selected devices.</p>
                <p>
                  Delivery time: <strong>{selectedDateTime?.format("MMMM D, YYYY h:mm A")}</strong>
                </p>
                <p>Timezone: {timeZone}</p>
              </>
            ) : (
              <p>This message will be sent immediately to {selectedDeviceIds.length} selected devices.</p>
            )}
          </AdminMessageActionDialog>
        )}
        {result && (
          <AdminMessageActionDialog
            title={result.title}
            confirmText="Close"
            onConfirm={() => setResult(null)}
            onCancel={() => setResult(null)}
            isResult
          >
            <p>{result.message}</p>
          </AdminMessageActionDialog>
        )}
      </ContentWrapper>
    </>
  )
}

export const VendorMessageHistory = () => {
  const navigate = useNavigate()
  const { urlQueryParams } = useUrlQueryParams()
  const pageSize = getMessageCenterPageSize(urlQueryParams)
  const { data, isFetching, isError } = useGetAdminMessageHistoryQuery({
    ...urlQueryParams,
    perPage: getMessageCenterPageSize(urlQueryParams),
  })
  return (
    <ContentWrapper
      title="Message history"
      breadcrumbs={[{ label: "Message center", link: "/messages/center" }, { label: "Message history" }]}
      className={styles.content}
    >
      <div className={styles.historyHeader}>
        <ButtonNew>Export</ButtonNew>
      </div>
      <TableWrapper
        headerCells={historyHeaderCells}
        data={batchRows(data?.data || [])}
        isLoading={isFetching}
        emptyMessage={isError ? "Unable to load message history." : "No message history found."}
        onRowClick={id => navigate(`/messages/report?id=${id}`)}
      />
      <Pagination
        perPage={pageSize}
        meta={
          data?.meta || {
            current_page: 1,
            last_page: 1,
            from: data?.data.length ? 1 : 0,
            to: data?.data.length || 0,
            per_page: pageSize,
            total: data?.data.length || 0,
          }
        }
      />
    </ContentWrapper>
  )
}

export const VendorScheduledMessages = () => {
  const { urlQueryParams } = useUrlQueryParams()
  const pageSize = getMessageCenterPageSize(urlQueryParams)
  const { data, isFetching, isError } = useGetAdminScheduledMessagesQuery({
    ...urlQueryParams,
    perPage: getMessageCenterPageSize(urlQueryParams),
  })
  const [sendNow, { isLoading: isSending }] = useSendScheduledAdminMessageNowMutation()
  const [cancel, { isLoading: isCanceling }] = useCancelScheduledAdminMessageMutation()
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null)
  const [action, setAction] = useState<"send" | "cancel" | null>(null)
  const navigate = useNavigate()

  const runAction = async () => {
    if (!selectedBatch || !action) return
    if (action === "send") await sendNow(selectedBatch)
    else await cancel(selectedBatch)
    setSelectedBatch(null)
    setAction(null)
  }

  return (
    <ContentWrapper
      title="Scheduled messages"
      breadcrumbs={[{ label: "Message center", link: "/messages/center" }, { label: "Scheduled messages" }]}
      className={styles.content}
    >
      <TableWrapper
        headerCells={historyHeaderCells}
        data={batchRows(data?.data || [])}
        isLoading={isFetching}
        emptyMessage={isError ? "Unable to load scheduled messages." : undefined}
        onRowClick={id => navigate(`/messages/report?id=${id}`)}
        getRowActions={rowData => {
          const id = rowData.rowSettings.id
          const isScheduled = rowData.status?.value === "SCHEDULED"
          return {
            actionButtons: [
              {
                icon: "▶",
                tooltip: "Send now",
                hidden: !isScheduled,
                handler: () => {
                  setSelectedBatch(id)
                  setAction("send")
                },
              },
              {
                icon: "×",
                tooltip: "Cancel schedule",
                hidden: !isScheduled,
                handler: () => {
                  setSelectedBatch(id)
                  setAction("cancel")
                },
              },
            ],
          }
        }}
      />
      <Pagination meta={data?.meta} perPage={pageSize} />
      {action && (
        <AdminMessageActionDialog
          title={action === "send" ? "Send Scheduled Message Now?" : "Cancel Scheduled Message?"}
          confirmText={action === "send" ? "Send now" : "Cancel schedule"}
          onConfirm={runAction}
          onCancel={() => {
            setSelectedBatch(null)
            setAction(null)
          }}
          isFetching={isSending || isCanceling}
        >
          <p>
            {action === "send"
              ? "This scheduled message will be sent immediately."
              : "This will cancel the scheduled message. Previously sent messages will remain in history."}
          </p>
        </AdminMessageActionDialog>
      )}
    </ContentWrapper>
  )
}

export const VendorMessageReport = () => {
  const id = Number(new URLSearchParams(window.location.search).get("id"))
  const { data, isFetching } = useGetAdminMessageQuery(id, { skip: !id })

  return (
    <ContentWrapper
      title="Message details"
      breadcrumbs={[
        { label: "Message center", link: "/messages/center" },
        { label: "Message history", link: "/messages/history" },
        { label: data?.data?.title || "Message details" },
      ]}
      className={styles.content}
    >
      <div className={styles.summary}>
        <div>
          <small>Date</small>
          <b>{parseDateField(data?.data?.scheduled_at, "L LT") || "-"}</b>
        </div>
        <div>
          <small>Title</small>
          <b>{data?.data?.title || "-"}</b>
        </div>
        <div>
          <small>Message</small>
          <b>{data?.data?.status || "-"}</b>
        </div>
        <div>
          <small>Delivered</small>
          <b className={styles.delivered}>{data?.data?.delivered ?? 0}</b>
        </div>
        <div>
          <small>Pending</small>
          <b className={styles.pending}>{data?.data?.pending ?? 0}</b>
        </div>
        <div>
          <small>Failed</small>
          <b className={styles.failed}>{data?.data?.failed ?? 0}</b>
        </div>
      </div>
      <TableWrapper
        headerCells={adminMessageTableHeaderCells}
        data={convertAdminMessageDevicesToTableRows(data?.data?.devices)}
        isLoading={isFetching}
        rowsExpanded
      />
    </ContentWrapper>
  )
}

export const VendorMessageTemplate = () => {
  const [library, setLibrary] = useState(false)
  const [title, setTitle] = useState("")
  const [message, setMessage] = useState("")
  const [richMessage, setRichMessage] = useState("")
  const [repeat, setRepeat] = useState<"never" | "1_month" | "6_months" | "12_months">("never")
  const location = useLocation()
  const navigate = useNavigate()
  const templateId = Number(new URLSearchParams(location.search).get("id"))
  const { data: templateData } = useGetAdminMessageTemplateQuery(templateId, { skip: !templateId })
  const { data: templatesData } = useGetAdminMessageTemplatesQuery(undefined)
  const [createTemplate, { isLoading: isCreating }] = useCreateAdminMessageTemplateMutation()
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateAdminMessageTemplateMutation()
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteAdminMessageTemplateMutation()
  const [duplicateTemplate, { isLoading: isDuplicating }] = useDuplicateAdminMessageTemplateMutation()
  const [templateAction, setTemplateAction] = useState<"delete" | "duplicate" | null>(null)
  const [templateActionId, setTemplateActionId] = useState<number | null>(null)

  useEffect(() => {
    const template = templateData?.data
    if (template) {
      setTitle(template.title)
      setMessage(template.content_plain || "")
      setRichMessage(template.content_rich || "")
      setRepeat(template.repeat_period || "never")
    }
  }, [templateData])

  const saveTemplate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const body = {
      title,
      content_plain: message,
      content_rich: richMessage,
      repeat_period: repeat,
      ...(templateId ? { version: (templateData?.data.version ?? 1) + 1 } : {}),
    }
    if (templateId) await updateTemplate({ id: templateId, body })
    else await createTemplate(body)
    navigate("/messages/center")
  }

  const runTemplateAction = async () => {
    if (!templateActionId || !templateAction) return
    try {
      const response =
        templateAction === "delete" ? await deleteTemplate(templateActionId) : await duplicateTemplate(templateActionId)
      const error = getErrorMessage(response.error)
      if (error) {
        toast.error(error)
        return
      }

      setTemplateAction(null)
      setTemplateActionId(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to complete this template action.")
    }
  }
  return (
    <ContentWrapper
      title={templateId ? "Edit message template" : "Create message template"}
      breadcrumbs={[{ label: "Message center", link: "/messages/center" }, { label: "My templates" }]}
      className={styles.content}
      renderHeaderAccessoryView={() => (
        <ButtonNew
          className={styles.templateBackButton}
          color="greyBordered"
          onClick={() => navigate("/messages/center")}
        >
          <ArrowLeftIcon />
          Back
        </ButtonNew>
      )}
    >
      <div className={styles.templateLayout}>
        <form className={styles.formCard} onSubmit={saveTemplate}>
          <input
            value={title}
            onChange={event => setTitle(event.target.value)}
            placeholder="Type a title..."
            maxLength={18}
          />
          <small>{title.length}/18</small>
          <label>Message</label>
          <MarkdownEditor
            value={richMessage || message}
            onChange={(rich, plainMessage) => {
              setRichMessage(rich)
              setMessage(plainMessage)
            }}
          />
          <small>{message.length}/220</small>
          <label>Repeat every ⓘ</label>
          <div className={styles.repeat}>
            {(
              [
                { value: "never", label: "Does not repeat" },
                { value: "1_month", label: "1 month" },
                { value: "6_months", label: "6 months" },
                { value: "12_months", label: "12 months" },
              ] as const
            ).map(option => (
              <button
                key={option.value}
                type="button"
                className={repeat === option.value ? styles.activeTab : ""}
                onClick={() => setRepeat(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className={styles.formActions}>
            <ButtonNew onClick={() => navigate("/messages/center")}>Cancel</ButtonNew>
            <ButtonNew
              color="blue"
              type="submit"
              disabled={isCreating || isUpdating || !title.trim() || !message.trim()}
            >
              Save
            </ButtonNew>
          </div>
        </form>
        <div className={styles.templateCard}>
          <div className={styles.tabs}>
            <button className={!library ? styles.activeTab : ""} onClick={() => setLibrary(false)}>
              My templates
            </button>
            <button className={library ? styles.activeTab : ""} onClick={() => setLibrary(true)}>
              Templates library
            </button>
          </div>
          {library ? (
            <>
              <div className={styles.templateHeader}>
                <span>ID</span>
                <span>Title</span>
                <span>Message</span>
                <span>Actions</span>
              </div>
              {(templatesData?.data || []).map(template => (
                <div className={styles.templateItem} key={template.id}>
                  <span>{template.id}</span>
                  <span>{template.title}</span>
                  <span className={styles.templateMessage}>{template.content_plain || "-"}</span>
                  <div className={styles.templateActions}>
                    <button
                      type="button"
                      className={`${styles.templateAction} ${styles.templateAddAction}`}
                      aria-label={`Duplicate ${template.title}`}
                      onClick={() => {
                        setTemplateActionId(template.id)
                        setTemplateAction("duplicate")
                      }}
                    >
                      <PlusIcon />
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className={styles.templateHeader}>
                <span>Title</span>
                <span>Create date</span>
                <span>Message</span>
                <span>Actions</span>
              </div>
              {(templatesData?.data || []).map(template => (
                <div className={styles.templateItem} key={template.id}>
                  <span className={styles.templateTitle}>{template.title}</span>
                  <span className={styles.templateDate}>{parseDateField(template.created_at) || "-"}</span>
                  <span className={styles.templateMessage}>{template.content_plain || "-"}</span>
                  <div className={styles.templateActions}>
                    <button
                      type="button"
                      className={styles.templateAction}
                      aria-label={`Edit ${template.title}`}
                      onClick={() => navigate(`/messages/edit-template?id=${template.id}`)}
                    >
                      <PencilIcon />
                    </button>
                    <button
                      type="button"
                      className={`${styles.templateAction} ${styles.deleteTemplateAction}`}
                      aria-label={`Delete ${template.title}`}
                      onClick={() => {
                        setTemplateActionId(template.id)
                        setTemplateAction("delete")
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      {templateAction && (
        <AdminMessageActionDialog
          title={templateAction === "delete" ? "Delete Template?" : "Duplicate Template?"}
          confirmText={templateAction === "delete" ? "Delete and cancel messages" : "Duplicate"}
          onConfirm={runTemplateAction}
          onCancel={() => {
            setTemplateAction(null)
            setTemplateActionId(null)
          }}
          isFetching={isDeleting || isDuplicating}
        >
          <p>
            {templateAction === "delete"
              ? "This template is currently used by one or more scheduled messages. Deleting it will cancel all associated scheduled messages. Previously sent messages will remain in the message history."
              : "Create a copy of this template for editing and reuse."}
          </p>
        </AdminMessageActionDialog>
      )}
    </ContentWrapper>
  )
}
